import { Icon } from './Icon';
import type { ReactNode } from 'react';

export interface FilterBarProps {
  search?: string;
  onSearch?: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  lang?: 'he' | 'en';
  /** 'md' (default, 36px) keeps every existing screen unchanged; 'lg' gives a
   *  taller, more prominent search that won't get squeezed by adjacent filters. */
  size?: 'md' | 'lg';
  children?: ReactNode;
}

export function FilterBar({
  search, onSearch, onClear, placeholder, lang = 'he', size = 'md', children,
}: FilterBarProps) {
  const lg = size === 'lg';
  const inputHeight = lg ? 44 : 38;
  const iconSize = lg ? 18 : 16;
  const iconInset = lg ? 12 : 10;
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--gap)',
        alignItems: 'center',
        padding: 'var(--pad-card)',
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-3)',
        flexWrap: 'wrap',
      }}
    >
      {/* On lg, keep the search a real, generous field even beside filters:
          grow to fill, but never shrink below a comfortable typing width. */}
      <div style={{ flex: lg ? '1 1 320px' : 1, minWidth: lg ? 260 : undefined, position: 'relative' }}>
        <span
          style={{
            position: 'absolute',
            insetInlineStart: iconInset,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--muted)',
            pointerEvents: 'none',
          }}
        >
          <Icon name="search" size={iconSize} />
        </span>
        <input
          type="search"
          value={search ?? ''}
          onChange={(e) => onSearch?.(e.target.value)}
          placeholder={placeholder ?? (lang === 'he' ? 'חיפוש…' : 'Search…')}
          style={{
            width: '100%',
            height: inputHeight,
            paddingInlineStart: lg ? 42 : 36,
            paddingInlineEnd: lg ? 14 : 12,
            borderRadius: 'var(--r-2)',
            border: '1px solid var(--line)',
            background: 'var(--surface-2)',
            color: 'var(--text)',
            fontSize: lg ? 'var(--t-lg)' : 'var(--t-md)',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
      </div>
      {children}
      {(search || onClear) && (
        <button type="button" className="btn ghost" onClick={onClear}>
          {lang === 'he' ? 'נקה' : 'Clear'}
        </button>
      )}
    </div>
  );
}

export interface SelectProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  placeholder?: string;
}

export function Select<T extends string>({ value, onChange, options, placeholder }: SelectProps<T>) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      style={{
        height: 38,
        padding: '0 12px',
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--glass-line-2)',
        background: 'var(--surface)',
        color: 'var(--text)',
        fontSize: 'var(--t-md)',
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
